import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { Modal, ConfirmDialog, LoadingSpinner } from '../components/shared';

const RUOLO_COLORS = {
  admin:          '#7C3AED',
  commercialista: '#2563EB',
  collaboratore:  '#059669',
  readonly:       '#6B7280',
};


// ─── PAGINA PERMESSI UTENTE (full page) ──────────────────────────────────────
function PaginaPermessi({ utente, onBack, addToast }) {
  const [moduli, setModuli] = useState([]);
  const [bilanci, setBilanci] = useState([]);
  const [permessi, setPermessi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedModuli, setExpandedModuli] = useState(new Set());

  useEffect(() => {
    Promise.all([
      fetch('/api/utenti/moduli', { headers:{ Authorization:'Bearer '+(localStorage.getItem('sp_token')||'') } }).then(r=>r.json()),
      fetch(`/api/utenti/${utente.id}/permessi`, { headers:{ Authorization:'Bearer '+(localStorage.getItem('sp_token')||'') } }).then(r=>r.json()),
    ]).then(([mRes, pRes]) => {
      if (mRes.success) { setModuli(mRes.data.moduli||[]); setBilanci(mRes.data.bilanci||[]); }
      if (pRes.success) {
        setPermessi(pRes.data||[]);
        // Espandi automaticamente i moduli già attivi con filtro
        const exp = new Set();
        (pRes.data||[]).forEach(p => { if(p.filtro_ids) exp.add(p.modulo); });
        setExpandedModuli(exp);
      }
    }).finally(() => setLoading(false));
  }, [utente.id]);

  const haModulo  = (key) => permessi.some(p => p.modulo === key);
  const getAccesso = (key) => permessi.find(p=>p.modulo===key)?.accesso || 'lettura';
  const getFiltro  = (key) => { try { return JSON.parse(permessi.find(p=>p.modulo===key)?.filtro_ids||'[]'); } catch(e) { return []; } };

  const toggleModulo = (key) => {
    if (haModulo(key)) { setPermessi(p => p.filter(x=>x.modulo!==key)); setExpandedModuli(p=>{ const n=new Set(p); n.delete(key); return n; }); }
    else setPermessi(p => [...p, { modulo:key, accesso:'lettura', filtro_ids:null }]);
  };

  const setAccesso = (key, accesso) => setPermessi(p => p.map(x=>x.modulo===key?{...x,accesso}:x));

  const toggleExpand = (key) => setExpandedModuli(p => { const n=new Set(p); n.has(key)?n.delete(key):n.add(key); return n; });

  const toggleFiltroItem = (modulo, id) => {
    const ids = getFiltro(modulo);
    const nuovi = ids.includes(id) ? ids.filter(x=>x!==id) : [...ids, id];
    setPermessi(p => p.map(x=>x.modulo===modulo?{...x, filtro_ids: nuovi.length?JSON.stringify(nuovi):null}:x));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/utenti/${utente.id}/permessi`, {
        method:'PUT',
        headers:{ 'Content-Type':'application/json', Authorization:'Bearer '+(localStorage.getItem('sp_token')||'') },
        body: JSON.stringify({ permessi })
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      addToast('Permessi salvati', 'success');
      onBack();
    } catch(e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  // Moduli con sottofiltro disponibile
  const SOTTOFILTRI = { bilanci: bilanci };

  if (loading) return <div className="page-content"><div style={{ padding:40, textAlign:'center' }}>⏳ Caricamento…</div></div>;

  return (
    <div className="page-content">
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
        <button className="btn btn-sm" onClick={onBack}>← Utenti</button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:17, fontWeight:700 }}>🔐 Permessi — {utente.nome}</div>
          <div style={{ fontSize:12, color:'var(--text-faint)' }}>{utente.username} · Ruolo: Collaboratore</div>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? '⏳ Salvataggio…' : '✓ Salva permessi'}
        </button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        {moduli.map(m => {
          const attivo   = haModulo(m.key);
          const accesso  = getAccesso(m.key);
          const filtroIds = getFiltro(m.key);
          const expanded  = expandedModuli.has(m.key);
          const sottofiltro = SOTTOFILTRI[m.key];
          const haFiltro = sottofiltro && sottofiltro.length > 0;

          return (
            <div key={m.key} style={{
              border:`2px solid ${attivo?'var(--accent)':'var(--border)'}`,
              borderRadius:'var(--radius)', overflow:'hidden',
              background: attivo?'var(--surface)':'var(--bg)',
              transition:'border 0.15s',
            }}>
              {/* Riga principale modulo */}
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', cursor:'pointer' }}
                onClick={() => toggleModulo(m.key)}>
                <input type="checkbox" checked={attivo} onChange={()=>toggleModulo(m.key)}
                  style={{ accentColor:'var(--accent)', width:16, height:16, cursor:'pointer', flexShrink:0 }}
                  onClick={e=>e.stopPropagation()} />
                <span style={{ fontWeight:600, fontSize:13, flex:1, color: attivo?'var(--text)':'var(--text-muted)' }}>
                  {m.label}
                </span>
                {attivo && (
                  <div style={{ display:'flex', gap:4 }} onClick={e=>e.stopPropagation()}>
                    {['lettura','scrittura'].map(a => (
                      <button key={a} onClick={()=>setAccesso(m.key,a)}
                        style={{ padding:'3px 10px', fontSize:11, borderRadius:10, cursor:'pointer',
                          border:`1px solid ${accesso===a?'var(--accent)':'var(--border-strong)'}`,
                          background: accesso===a?'var(--accent)':'transparent',
                          color: accesso===a?'white':'var(--text-muted)', fontWeight:500 }}>
                        {a}
                      </button>
                    ))}
                  </div>
                )}
                {/* Toggle sottofiltro se disponibile */}
                {attivo && haFiltro && (
                  <button onClick={e=>{ e.stopPropagation(); toggleExpand(m.key); }}
                    style={{ background:'none', border:'1px solid var(--border)', borderRadius:4,
                      padding:'2px 6px', fontSize:11, cursor:'pointer', color:'var(--text-muted)' }}>
                    {filtroIds.length>0?`${filtroIds.length} selezionati`:'Tutti'} {expanded?'▾':'▸'}
                  </button>
                )}
              </div>

              {/* Sottofiltro espanso */}
              {attivo && haFiltro && expanded && (
                <div style={{ padding:'8px 14px 12px', borderTop:'1px solid var(--border)', background:'var(--bg)' }}>
                  <div style={{ fontSize:11, color:'var(--text-faint)', fontWeight:600, textTransform:'uppercase', marginBottom:6 }}>
                    Seleziona occorrenze (vuoto = accesso a tutti)
                  </div>
                  <div style={{ display:'flex', gap:4, flexWrap:'wrap', maxHeight:150, overflowY:'auto' }}>
                    {sottofiltro.map(item => {
                      const id   = item.id;
                      const nome = item.denominazione||item.ragione_sociale||[item.nome,item.cognome].filter(Boolean).join(' ')||'—';
                      const sel  = filtroIds.includes(id);
                      return (
                        <button key={id} onClick={()=>toggleFiltroItem(m.key, id)}
                          style={{ padding:'4px 10px', fontSize:11, borderRadius:10, cursor:'pointer',
                            border:`1.5px solid ${sel?'var(--accent)':'var(--border-strong)'}`,
                            background: sel?'var(--accent)':'transparent',
                            color: sel?'white':'var(--text-muted)', fontWeight:500, whiteSpace:'nowrap' }}>
                          {item.anno ? `${item.anno} — ${nome}` : nome}
                        </button>
                      );
                    })}
                  </div>
                  {filtroIds.length > 0 && (
                    <button onClick={()=>setPermessi(p=>p.map(x=>x.modulo===m.key?{...x,filtro_ids:null}:x))}
                      style={{ marginTop:6, fontSize:11, color:'var(--c-red)', background:'none', border:'none', cursor:'pointer' }}>
                      ✕ Rimuovi filtro (accesso a tutti)
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UtenteForm({ initial={}, ruoli={}, onSave, onClose, loading, isNew }) {
  const [d, setD] = useState({
    username: '', nome: '', email: '', ruolo: 'collaboratore', attivo: 1,
    password: '', password2: '', ...initial, password: '', password2: '',
  });
  const [err, setErr] = useState('');
  const set = (k,v) => setD(p => ({...p,[k]:v}));

  const submit = (e) => {
    e.preventDefault();
    setErr('');
    if (isNew && !d.password) { setErr('Password obbligatoria per nuovo utente'); return; }
    if (d.password && d.password !== d.password2) { setErr('Le password non coincidono'); return; }
    if (d.password && d.password.length < 6) { setErr('Password minimo 6 caratteri'); return; }
    const email = (d.email || '').trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setErr('Indirizzo email non valido'); return; }
    const data = { username: d.username, nome: d.nome, ruolo: d.ruolo, attivo: d.attivo ? 1 : 0 };
    if (d.password) data.password = d.password;
    onSave(data, email);
  };

  return (
    <form onSubmit={submit}>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Username *</label>
          <input className="form-control" value={d.username} onChange={e=>set('username',e.target.value)}
            required disabled={!isNew} placeholder="es. mario.rossi" />
        </div>
        <div className="form-group">
          <label className="form-label">Nome completo *</label>
          <input className="form-control" value={d.nome} onChange={e=>set('nome',e.target.value)}
            required placeholder="es. Mario Rossi" />
        </div>
        <div className="form-group form-full">
          <label className="form-label">Email</label>
          <input className="form-control" type="email" value={d.email}
            onChange={e=>set('email',e.target.value)} placeholder="es. mario.rossi@simonepalumbo.it" />
          <div style={{ fontSize:11, color:'var(--text-faint)', marginTop:4 }}>
            Serve per inviare la notifica quando gli viene assegnata un'attività o una sottoattività.
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Ruolo *</label>
          <select className="form-control" value={d.ruolo} onChange={e=>set('ruolo',e.target.value)}>
            {Object.entries(ruoli).map(([k,v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <div style={{ fontSize:11, color:'var(--text-faint)', marginTop:4 }}>
            {d.ruolo === 'admin' && '⚠ Accesso completo a tutto il sistema'}
            {d.ruolo === 'commercialista' && '✓ Accesso a tutte le sezioni tranne gestione utenti'}
            {d.ruolo === 'collaboratore' && '✓ Clienti, scadenze, attività, avvisi'}
            {d.ruolo === 'readonly' && '✓ Solo lettura — nessuna modifica'}
          </div>
        </div>
        <div className="form-group" style={{ display:'flex', alignItems:'center', gap:10, paddingTop:28 }}>
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13 }}>
            <input type="checkbox" checked={!!d.attivo} onChange={e=>set('attivo',e.target.checked?1:0)}
              style={{ accentColor:'var(--accent)', width:16, height:16 }} />
            Utente attivo
          </label>
        </div>
        <div className="form-group">
          <label className="form-label">{isNew ? 'Password *' : 'Nuova password (lascia vuoto per non cambiare)'}</label>
          <input className="form-control" type="password" value={d.password}
            onChange={e=>set('password',e.target.value)}
            placeholder={isNew ? 'Minimo 6 caratteri' : 'Lascia vuoto per non modificare'}
            required={isNew} />
        </div>
        <div className="form-group">
          <label className="form-label">Conferma password</label>
          <input className="form-control" type="password" value={d.password2}
            onChange={e=>set('password2',e.target.value)}
            placeholder="Ripeti la password" />
        </div>
      </div>
      {err && (
        <div style={{ padding:'8px 12px', background:'#FEF2F2', border:'1px solid #FECACA',
          borderRadius:'var(--radius-sm)', color:'#DC2626', fontSize:12, marginTop:8 }}>
          ⚠ {err}
        </div>
      )}
      <div className="modal-footer" style={{ padding:'16px 0 0', borderTop:'1px solid var(--border)', marginTop:16 }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Salvataggio…' : isNew ? 'Crea utente' : 'Salva modifiche'}
        </button>
      </div>
    </form>
  );
}

export default function Utenti({ addToast, currentUser }) {
  const [utenti, setUtenti]   = useState([]);
  const [ruoli, setRuoli]     = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [permessiItem, setPermessiItem] = useState(null);
  const [showPwd, setShowPwd]   = useState(false);
  const [pwd, setPwd]           = useState({ vecchia:'', nuova:'', nuova2:'' });

  const apiEmail = (path, opts={}) => fetch('/api'+path, {
    ...opts,
    headers:{ 'Content-Type':'application/json', Authorization:'Bearer '+(localStorage.getItem('sp_token')||'') },
  }).then(r=>r.json()).then(j => { if (!j.success) throw new Error(j.error || 'Errore'); return j.data; });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.getUtenti(),
      apiEmail('/utenti-email').catch(() => ({})),
    ]).then(([d, mail]) => {
        setUtenti((d.utenti||[]).map(u => ({ ...u, email: mail[u.id] || '' })));
        setRuoli(d.ruoli||{});
      })
      .catch(e => addToast(e.message, 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (data, email) => {
    setSaving(true);
    try {
      if (editItem) {
        await api.updateUtente(editItem.id, data);
        await apiEmail(`/utenti-email/${editItem.id}`, { method:'PUT', body: JSON.stringify({ email }) });
        addToast('Utente aggiornato', 'success'); setEditItem(null);
      } else {
        const nuovo = await api.createUtente(data);
        if (email) {
          let nid = nuovo && nuovo.id;
          if (!nid) { const l = await api.getUtenti(); nid = ((l.utenti||[]).find(x => x.username === data.username)||{}).id; }
          if (nid) await apiEmail(`/utenti-email/${nid}`, { method:'PUT', body: JSON.stringify({ email }) });
        }
        addToast('Utente creato', 'success'); setShowForm(false);
      }
      load();
    } catch(e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.deleteUtente(delItem.id); addToast('Utente eliminato', 'success'); setDelItem(null); load(); }
    catch(e) { addToast(e.message, 'error'); }
  };

  const handlePwd = async (e) => {
    e.preventDefault();
    if (pwd.nuova !== pwd.nuova2) { addToast('Le password non coincidono', 'error'); return; }
    if (pwd.nuova.length < 6) { addToast('Password minimo 6 caratteri', 'error'); return; }
    setSaving(true);
    try {
      await api.changePassword({ vecchia: pwd.vecchia, nuova: pwd.nuova });
      addToast('Password aggiornata', 'success');
      setShowPwd(false); setPwd({ vecchia:'', nuova:'', nuova2:'' });
    } catch(e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  if (permessiItem) return (
    <PaginaPermessi utente={permessiItem} onBack={()=>setPermessiItem(null)} addToast={addToast} />
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <div className="page-title">👥 Gestione Utenti</div>
          <div className="page-subtitle">{utenti.length} utenti registrati</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn" onClick={() => setShowPwd(true)}>🔑 Cambia password</button>
          {currentUser?.ruolo === 'admin' && (
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Nuovo utente</button>
          )}
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Utente</th><th>Nome</th><th>Email</th><th>Ruolo</th><th>Stato</th>
                <th>Ultimo accesso</th><th>Azioni</th>
              </tr></thead>
              <tbody>
                {utenti.map(u => (
                  <tr key={u.id} style={{ opacity: u.attivo ? 1 : 0.5 }}>
                    <td style={{ fontFamily:'monospace', fontSize:13, fontWeight:500 }}>
                      {u.username}
                      {u.id === currentUser?.id && (
                        <span style={{ marginLeft:6, fontSize:10, padding:'2px 6px',
                          background:'var(--accent-light)', color:'var(--accent)',
                          borderRadius:10, fontFamily:'inherit' }}>tu</span>
                      )}
                    </td>
                    <td className="fw-500">{u.nome}</td>
                    <td style={{ fontSize:12 }}>
                      {u.email || <span style={{ color:'#DC2626' }} title="Senza email non riceve le notifiche">— manca —</span>}
                    </td>
                    <td>
                      <span style={{
                        padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:500,
                        background: (RUOLO_COLORS[u.ruolo]||'#6B7280')+'22',
                        color: RUOLO_COLORS[u.ruolo]||'#6B7280',
                      }}>
                        {ruoli[u.ruolo]?.label || u.ruolo}
                      </span>
                    </td>
                    <td>
                      <span className={`pill ${u.attivo ? 'pill-green' : 'pill-gray'}`}>
                        {u.attivo ? 'Attivo' : 'Disabilitato'}
                      </span>
                    </td>
                    <td style={{ fontSize:12, color:'var(--text-faint)' }}>
                      {u.last_login ? new Date(u.last_login).toLocaleString('it-IT') : 'Mai'}
                    </td>
                    <td>
                      {currentUser?.ruolo === 'admin' && (
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-sm" onClick={() => setEditItem(u)}>✎</button>
                          {u.ruolo === 'collaboratore' && (
                            <button className="btn btn-sm" title="Gestisci permessi" onClick={() => setPermessiItem(u)}>🔐</button>
                          )}
                          {u.id !== currentUser?.id && (
                            <button className="btn btn-sm btn-danger" onClick={() => setDelItem(u)}>×</button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Legenda ruoli */}
      <div className="card" style={{ marginTop:16 }}>
        <div className="card-header"><span className="card-title">Permessi per ruolo</span></div>
        <div className="card-body">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12 }}>
            {Object.entries(ruoli).map(([k,v]) => (
              <div key={k} style={{ padding:'10px 14px', borderRadius:'var(--radius-sm)',
                border:`1px solid ${RUOLO_COLORS[k]||'#6B7280'}44`,
                background: (RUOLO_COLORS[k]||'#6B7280')+'0d' }}>
                <div style={{ fontWeight:600, fontSize:13, color:RUOLO_COLORS[k]||'#6B7280',
                  marginBottom:4 }}>{v.label}</div>
                <div style={{ fontSize:11, color:'var(--text-faint)' }}>
                  {k==='admin' && 'Accesso completo a tutto il sistema inclusa gestione utenti'}
                  {k==='commercialista' && 'Accesso a clienti, scadenze, IVA, avvisi, F24, redditi, banche, OneDrive'}
                  {k==='collaboratore' && 'Accesso a clienti, scadenze, attività e avvisi'}
                  {k==='readonly' && 'Solo lettura su clienti, scadenze, IVA, F24, redditi e avvisi'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form nuovo/modifica utente */}
      {(showForm || editItem) && (
        <Modal title={editItem ? `Modifica ${editItem.nome}` : 'Nuovo Utente'}
          onClose={() => { setShowForm(false); setEditItem(null); }}>
          <UtenteForm initial={editItem||{}} ruoli={ruoli}
            onSave={handleSave} onClose={() => { setShowForm(false); setEditItem(null); }}
            loading={saving} isNew={!editItem} />
        </Modal>
      )}

      {/* Cambio password */}
      {showPwd && (
        <Modal title="🔑 Cambia password" onClose={() => setShowPwd(false)} size="sm">
          <form onSubmit={handlePwd}>
            <div className="form-grid">
              {[['vecchia','Password attuale'], ['nuova','Nuova password'], ['nuova2','Conferma nuova password']].map(([k,l]) => (
                <div key={k} className="form-group form-full">
                  <label className="form-label">{l}</label>
                  <input className="form-control" type="password" value={pwd[k]}
                    onChange={e => setPwd(p=>({...p,[k]:e.target.value}))}
                    required minLength={k!=='vecchia'?6:1} />
                </div>
              ))}
            </div>
            <div className="modal-footer" style={{ padding:'16px 0 0', borderTop:'1px solid var(--border)', marginTop:16 }}>
              <button type="button" className="btn" onClick={()=>setShowPwd(false)}>Annulla</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Salvataggio…' : 'Aggiorna password'}
              </button>
            </div>
          </form>
        </Modal>
      )}


      {delItem && (
        <ConfirmDialog
          message={`Eliminare l'utente "${delItem.nome}" (${delItem.username})?`}
          onConfirm={handleDelete} onCancel={() => setDelItem(null)} />
      )}
    </div>
  );
}
